/**
 * «Tu rutina» (card #547): helpers de texto y fecha compartidos por el registro,
 * el Inicio, la hoja «Tu racha», el resumen de la charla y el Perfil, y un hook
 * con caché de sesión para no pedir `/routine` en cada pantalla.
 */
import { useCallback, useEffect, useState } from 'react';
import { getRoutine, type CefrLevel, type Routine } from './englishApi.js';
import { A0_LEGACY_APP_ENABLED } from '../screens/a0/legacy.js';

/** Las cuatro paradas de la llama: nombre, minutos y días que propone. */
export const ROUTINE_LEVELS = [
  { name: 'Un cafecito', minutes: 5, weekdays: [0, 2, 4] },
  { name: 'Un recreo', minutes: 10, weekdays: [0, 1, 2, 3, 4] },
  { name: 'Un capítulo', minutes: 15, weekdays: [0, 1, 2, 3, 4] },
  { name: 'A fondo', minutes: 20, weekdays: [0, 1, 2, 3, 4, 5, 6] },
] as const;

export const WEEKDAY_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;
const WEEKDAY_ABBR = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'] as const;

/** Horas que ofrece el aviso (null = sin aviso). */
export const REMINDER_HOURS: readonly (number | null)[] = [8, 13, 19, 21, null];

/**
 * «lun a vie», «todos los días», «lun, mié y vie», «lun a sáb», «lun a mié y vie»:
 * tres o más días seguidos se dicen como tramo.
 */
export function formatWeekdays(weekdays: readonly number[]): string {
  const d = [...new Set(weekdays)].sort((a, b) => a - b);
  if (d.length === 7) return 'todos los días';
  if (d.join() === '5,6') return 'sáb y dom';
  const runs: number[][] = [];
  for (const day of d) {
    const last = runs.at(-1);
    if (last && last.at(-1) === day - 1) last.push(day);
    else runs.push([day]);
  }
  const parts = runs.flatMap((r) =>
    r.length >= 3
      ? [`${WEEKDAY_ABBR[r[0] ?? 0]} a ${WEEKDAY_ABBR[r.at(-1) ?? 0]}`]
      : r.map((i) => WEEKDAY_ABBR[i] ?? ''),
  );
  if (parts.length === 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} y ${parts.at(-1) ?? ''}`;
}

const WEEKDAY_ONE = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
  'domingo',
] as const;
const WEEKDAY_MANY = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábados',
  'domingos',
] as const;

/** Para la frase del trato: «de lunes a viernes», «todos los días», «los lunes, miércoles y viernes». */
export function formatWeekdaysLong(weekdays: readonly number[]): string {
  const d = [...new Set(weekdays)].sort((a, b) => a - b);
  if (d.length === 7) return 'todos los días';
  const first = d[0] ?? 0;
  const last = d.at(-1) ?? 0;
  if (d.length >= 3 && last - first === d.length - 1)
    return `de ${WEEKDAY_ONE[first]} a ${WEEKDAY_ONE[last]}`;
  const names = d.map((i) => WEEKDAY_MANY[i] ?? '');
  return `los ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} y ${names.at(-1) ?? ''}`}`;
}

export function formatHour(hour: number | null): string {
  return hour === null ? 'sin aviso' : `${hour}:00`;
}

/** «10 min · lun a vie · aviso 19:00». */
export function formatRoutine(r: Pick<Routine, 'minutes' | 'weekdays' | 'reminderHour'>): string {
  const aviso = r.reminderHour === null ? 'sin aviso' : `aviso ${formatHour(r.reminderHour)}`;
  return `${r.minutes} min · ${formatWeekdays(r.weekdays)} · ${aviso}`;
}

/** Día de la semana de una fecha local con el lunes en 0. */
export function weekdayOf(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** ¿Es un día libre de la rutina? Sin rutina no hay días libres. */
export function isRestDay(routine: Pick<Routine, 'weekdays'> | null, date: Date): boolean {
  return routine !== null && !routine.weekdays.includes(weekdayOf(date));
}

const WEEKDAY_FULL = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
  'domingo',
] as const;

/** El próximo día de práctica después de hoy (lunes = 0), por nombre: «lunes». */
export function nextPracticeDay(weekdays: readonly number[], todayIdx: number): string {
  for (let k = 1; k <= 7; k++) {
    const wd = (todayIdx + k) % 7;
    if (weekdays.includes(wd)) return WEEKDAY_FULL[wd] ?? '';
  }
  throw new Error('nextPracticeDay: una rutina necesita al menos un día');
}

/** Horas de inglés hablado en un año con esa rutina (minutos × días × 52). */
export function hoursPerYear(minutes: number, daysPerWeek: number): number {
  return Math.round((minutes * daysPerWeek * 52) / 60);
}

let cache: Routine | null | undefined;

/** Invalida/actualiza la caché tras guardar (registro o Perfil). */
export function setRoutineCache(r: Routine | null): void {
  cache = r;
}

export interface UseRoutine {
  /** `undefined` = cargando; `null` = sin rutina. */
  routine: Routine | null | undefined;
  reload: () => void;
}

export function useRoutine(): UseRoutine {
  const [routine, setRoutine] = useState<Routine | null | undefined>(cache);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    getRoutine().then(
      (r) => {
        cache = r;
        if (alive) setRoutine(r);
      },
      () => {
        // Sin dato: las superficies se ven como sin rutina (no se inventa una).
        if (alive) setRoutine((cur) => (cur === undefined ? null : cur));
      },
    );
    return () => {
      alive = false;
    };
  }, [tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { routine, reload };
}

// ─── Cuándo se ofrece el paso «¿Cuánto le ponés?» ────────────────────────────
// Va DESPUÉS de la prueba de nivel (decisión de Emmanuel, 19/10): quien
// termina en A0 no lo ve mientras la app A0 aparte esté encendida (su Inicio no
// tiene racha ni meta; con ella dormida, plan 48, el A0 usa el Home normal y sí
// lo ve), y a los demás se les ofrece una sola vez. «Ya se lo ofrecimos» se recuerda por cuenta en este
// dispositivo; si ya tiene rutina, tampoco se le ofrece.

const ASKED_KEY = 'orborix.routineOffered';

function askedKey(userId: string): string {
  return `${ASKED_KEY}:${userId}`;
}

/** Marca que a esta cuenta ya se le ofreció el paso (comprometido o «Ahora no»). */
export function markRoutineOffered(userId: string): void {
  try {
    localStorage.setItem(askedKey(userId), '1');
  } catch (err) {
    console.warn('[routine] no se pudo recordar que se ofreció el paso', err);
  }
}

/**
 * Adónde ir al terminar la prueba de nivel: `/onboarding/routine` si
 * corresponde ofrecer la rutina, si no `/home`. Si `/routine` falla, no se
 * frena la entrada a la app: se sigue al Inicio (y se ofrecerá en otra vuelta).
 */
export async function pathAfterLevel(
  cefr: CefrLevel | null,
  userId: string | null,
): Promise<string> {
  if (cefr === null || (A0_LEGACY_APP_ENABLED && cefr === 'A0') || userId === null) return '/home';
  try {
    if (localStorage.getItem(askedKey(userId))) return '/home';
  } catch {
    // Sin localStorage: se decide sólo por si ya tiene rutina.
  }
  try {
    return (await getRoutine()) ? '/home' : '/onboarding/routine';
  } catch (err) {
    console.warn('[routine] no se pudo leer la rutina al salir del nivel', err);
    return '/home';
  }
}
